import React, { useState, useEffect } from 'react';
import { projectFirestore } from '../../firebase/config';
import { CLOUDINARY_UPLOAD_PRESET, CLOUDINARY_UPLOAD_URL } from '../../cloudinary/config';

const NewsManager = () => {
    const [newsItems, setNewsItems] = useState([]);
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editTitle, setEditTitle] = useState('');
    const [editContent, setEditContent] = useState('');

    useEffect(() => {
        const unsub = projectFirestore.collection('news')
            .orderBy('order', 'asc')
            .onSnapshot((snap) => {
                setNewsItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            });
        return () => unsub();
    }, []);

    const uploadToCloudinary = (file) => {
        return new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
            formData.append('folder', 'news-images');
            const xhr = new XMLHttpRequest();
            xhr.open('POST', CLOUDINARY_UPLOAD_URL);
            xhr.onload = () => {
                if (xhr.status === 200) resolve(JSON.parse(xhr.responseText).secure_url);
                else reject(new Error('فشل الرفع'));
            };
            xhr.onerror = () => reject(new Error('خطأ في الاتصال'));
            xhr.send(formData);
        });
    };

    const handleAdd = async () => {
        if (!title.trim() && !imageFile) { alert('أضف عنوان أو صورة على الأقل'); return; }
        setUploading(true);
        try {
            let imageUrl = '';
            if (imageFile) imageUrl = await uploadToCloudinary(imageFile);
            await projectFirestore.collection('news').add({
                title: title.trim(),
                content: content.trim(),
                imageUrl,
                order: newsItems.length,
                createdAt: new Date().toISOString(),
            });
            setTitle(''); setContent(''); setImageFile(null);
        } catch (err) {
            alert('خطأ: ' + err.message);
        }
        setUploading(false);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('هل تريد حذف هذا الخبر؟')) return;
        await projectFirestore.collection('news').doc(id).delete();
    };

    const handleStartEdit = (item) => {
        setEditingId(item.id);
        setEditTitle(item.title || '');
        setEditContent(item.content || '');
    };

    const handleSaveEdit = async (id) => {
        await projectFirestore.collection('news').doc(id).update({
            title: editTitle.trim(),
            content: editContent.trim(),
        });
        setEditingId(null);
    };

    const handleImageReplace = async (id, e) => {
        const file = e.target.files[0];
        if (!file) return;
        try {
            const url = await uploadToCloudinary(file);
            await projectFirestore.collection('news').doc(id).update({ imageUrl: url });
        } catch (err) {
            alert('خطأ في رفع الصورة: ' + err.message);
        }
        e.target.value = '';
    };

    const handleMove = async (index, direction) => {
        const swapIndex = index + direction;
        if (swapIndex < 0 || swapIndex >= newsItems.length) return;
        const batch = projectFirestore.batch();
        batch.update(projectFirestore.collection('news').doc(newsItems[index].id), { order: swapIndex });
        batch.update(projectFirestore.collection('news').doc(newsItems[swapIndex].id), { order: index });
        await batch.commit();
    };

    return (
        <div className="container my-4">
            <h4 className="text-center mb-4">إدارة الأخبار</h4>

            {/* إضافة خبر جديد */}
            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <h5 className="card-title mb-3">إضافة خبر جديد</h5>
                    <input type="text" className="form-control mb-2" placeholder="العنوان"
                        value={title} onChange={e => setTitle(e.target.value)} />
                    <textarea className="form-control mb-2" placeholder="المحتوى (اختياري)" rows={3}
                        value={content} onChange={e => setContent(e.target.value)} />
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <label className="btn btn-outline-secondary btn-sm mb-0" style={{ cursor: 'pointer' }}>
                            {imageFile ? imageFile.name : 'اختر صورة (اختياري)'}
                            <input type="file" accept="image/*" hidden onChange={e => setImageFile(e.target.files[0])} />
                        </label>
                        <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={uploading}>
                            {uploading ? 'جاري الإضافة...' : 'إضافة'}
                        </button>
                    </div>
                </div>
            </div>

            {/* قائمة الأخبار */}
            {newsItems.length === 0 && <p className="text-muted text-center">لا توجد أخبار بعد</p>}
            {newsItems.map((item, i) => (
                <div key={item.id} className="card shadow-sm mb-3">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                                {editingId === item.id ? (
                                    <>
                                        <input type="text" className="form-control mb-2" value={editTitle}
                                            onChange={e => setEditTitle(e.target.value)} placeholder="العنوان" />
                                        <textarea className="form-control mb-2" value={editContent}
                                            onChange={e => setEditContent(e.target.value)} rows={2} placeholder="المحتوى" />
                                        <div className="btn-group btn-group-sm">
                                            <button className="btn btn-success" onClick={() => handleSaveEdit(item.id)}>حفظ</button>
                                            <button className="btn btn-secondary" onClick={() => setEditingId(null)}>إلغاء</button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {item.title && <h6 className="mb-1">{item.title}</h6>}
                                        {item.content && <p className="text-muted mb-1" style={{ fontSize: 14 }}>{item.content}</p>}
                                        <small className="text-muted">{item.createdAt ? new Date(item.createdAt).toLocaleDateString('ar-EG') : ''}</small>
                                    </>
                                )}
                            </div>
                            {item.imageUrl && (
                                <img src={item.imageUrl} alt="" style={{ width: 100, height: 70, objectFit: 'cover', borderRadius: 8, marginRight: 12 }} />
                            )}
                        </div>
                        <div className="d-flex gap-1 mt-2 flex-wrap">
                            <button className="btn btn-outline-primary btn-sm" onClick={() => handleStartEdit(item)}>
                                <i className="fas fa-edit"></i> تعديل
                            </button>
                            <label className="btn btn-outline-info btn-sm mb-0" style={{ cursor: 'pointer' }}>
                                <i className="fas fa-image"></i> {item.imageUrl ? 'تغيير الصورة' : 'إضافة صورة'}
                                <input type="file" accept="image/*" hidden onChange={e => handleImageReplace(item.id, e)} />
                            </label>
                            {item.imageUrl && (
                                <button className="btn btn-outline-warning btn-sm" onClick={async () => {
                                    await projectFirestore.collection('news').doc(item.id).update({ imageUrl: '' });
                                }}>
                                    <i className="fas fa-times"></i> حذف الصورة
                                </button>
                            )}
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => handleMove(i, -1)} disabled={i === 0}>▲</button>
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => handleMove(i, 1)} disabled={i === newsItems.length - 1}>▼</button>
                            <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(item.id)}>
                                <i className="fas fa-trash"></i> حذف
                            </button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default NewsManager;
